import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Workflow, Mail, Users, Target, Calendar, Repeat } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function Automation() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: Mail,
      title: "Email Campaigns",
      description: "Automated email sequences that nurture leads and drive conversions"
    },
    {
      icon: Users,
      title: "Lead Management",
      description: "Capture, score, and route leads automatically to the right team members"
    },
    {
      icon: Target,
      title: "Customer Segmentation",
      description: "Automatically organize contacts based on behavior and preferences"
    },
    {
      icon: Calendar,
      title: "Appointment Scheduling",
      description: "Let customers book meetings without the back-and-forth"
    },
    {
      icon: Workflow,
      title: "Workflow Automation",
      description: "Connect your tools and eliminate manual data entry"
    },
    {
      icon: Repeat,
      title: "Follow-Up Sequences",
      description: "Never miss a follow-up with automated reminder systems"
    },
  ];

  const workflows = [
    {
      title: "New Lead Workflow",
      steps: ["Lead fills form", "Added to CRM", "Welcome email sent", "Assigned to sales rep", "Follow-up scheduled"]
    },
    {
      title: "Customer Onboarding",
      steps: ["Purchase complete", "Welcome series starts", "Resources sent", "Check-in scheduled", "Feedback requested"]
    },
    {
      title: "Re-engagement Campaign",
      steps: ["Inactive detected", "Special offer sent", "Reminder emails", "Last chance notice", "Win-back call scheduled"]
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-primary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">Marketing Automation</span> That Works
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Stop doing repetitive tasks manually. Automate your marketing and focus on strategy.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-secondary to-primary text-white"
              >
                Automate Your Marketing
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Automate Every Step
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-secondary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-secondary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Example Workflows */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Example Workflows
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {workflows.map((workflow, index) => (
                <motion.div
                  key={workflow.title}
                  className="p-6 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-xl font-semibold mb-4">{workflow.title}</h3>
                  <div className="space-y-3">
                    {workflow.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center text-white text-xs font-bold">
                          {stepIndex + 1}
                        </div>
                        <span className="text-sm text-muted-foreground">{step}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              The Impact of Automation
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { stat: "70%", label: "Time Saved on Repetitive Tasks" },
                { stat: "3x", label: "More Leads Converted" },
                { stat: "50%", label: "Reduction in Human Error" },
                { stat: "24/7", label: "Marketing That Never Sleeps" },
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.label}
                  className="p-6 rounded-lg bg-card border border-border text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="text-4xl font-bold text-gradient mb-2">{benefit.stat}</div>
                  <p className="text-muted-foreground">{benefit.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Automate Your Marketing?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's build workflows that save time, increase conversions, and scale with your business.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-secondary to-primary text-white"
            >
              Get Started Today
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
